import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Avatar, Tooltip, IconButton, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Refresh, CheckCircle, Cancel, BeachAccess, HelpOutline } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const STATUSES = [
  { key: 'Present',    label: 'Present',    bg: '#e8f5e9', color: '#2e7d32' },
  { key: 'Absent',     label: 'Absent',     bg: '#ffebee', color: '#c62828' },
  { key: 'Leave',      label: 'Leave',      bg: '#fff3e0', color: '#e65100' },
  { key: 'Not Marked', label: 'Not Marked', bg: '#f5f5f5', color: '#757575' },
];

export default function Attendance() {
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [activeTab, setActiveTab] = useState('Not Marked');
  const [selectedClass, setSelectedClass] = useState('');
  const [standard, setStandard] = useState('');
  const [section, setSection] = useState('');
  const [acYear, setAcYear] = useState('');
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [allStudents, setAllStudents] = useState([]); 
  const [records, setRecords] = useState([]);       
  const [counts, setCounts] = useState({ Present: 0, Absent: 0, Leave: 0, 'Not Marked': 0 });
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState({}); 

  const fmtTime = (t) => t ? dayjs(t).format('hh:mm A') : '—';

  useEffect(() => {
    const init = async () => {
      const [secData, acData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
      ]);
      setClasses(secData?.sections || []);
      const years = acData?.academic_years || [];
      setAcademicYears(years);
      const current = years.find(y => y.is_current) || years[0];
      if (current) setAcYear(current.value || '');
    };
    init();
  }, []);

  const loadAll = useCallback(async () => {
    if (!standard || !section || !acYear) return;
    setLoading(true);

    const studData = await InvokeApi(Config.main_url + apiList.getStudentsByStdSecAcYear, {
      standard, section, acYear,
    });
    const students = studData?.students || [];
    setAllStudents(students);

    const [presentData, absentData, leaveData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getAttendanceByDateStdSec, { date, status: 'Present', standard, section }),
      InvokeApi(Config.main_url + apiList.getAttendanceByDateStdSec, { date, status: 'Absent',  standard, section }),
      InvokeApi(Config.main_url + apiList.getAttendanceByDateStdSec, { date, status: 'Leave',   standard, section }),
    ]);

    const presentList = presentData?.attendance || [];
    const absentList  = absentData?.attendance  || [];
    const leaveList   = leaveData?.attendance   || [];

    const markedIds = new Set([
      ...presentList.map(a => String(a.student_id)),
      ...absentList.map(a => String(a.student_id)),
      ...leaveList.map(a => String(a.student_id)),
    ]);
    const notMarkedList = students.filter(s => !markedIds.has(String(s.ID)));

    setCounts({
      Present:      presentList.length,
      Absent:       absentList.length,
      Leave:        leaveList.length,
      'Not Marked': notMarkedList.length,
    });

    const tabMap = {
      Present:      presentList,
      Absent:       absentList,
      Leave:        leaveList,
      'Not Marked': notMarkedList,
    };
    setRecords(tabMap[activeTab] || []);
    setLoading(false);
  }, [standard, section, acYear, date, activeTab]);

  useEffect(() => {
    if (standard && section && acYear) loadAll();
  }, [standard, section, acYear, date]);

  const switchTab = async (tab) => {
    setActiveTab(tab);
    if (!standard || !section || !acYear) return;
    setLoading(true);

    const [presentData, absentData, leaveData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getAttendanceByDateStdSec, { date, status: 'Present', standard, section }),
      InvokeApi(Config.main_url + apiList.getAttendanceByDateStdSec, { date, status: 'Absent',  standard, section }),
      InvokeApi(Config.main_url + apiList.getAttendanceByDateStdSec, { date, status: 'Leave',   standard, section }),
    ]);

    const presentList = presentData?.attendance || [];
    const absentList  = absentData?.attendance  || [];
    const leaveList   = leaveData?.attendance   || [];

    const markedIds = new Set([
      ...presentList.map(a => String(a.student_id)),
      ...absentList.map(a => String(a.student_id)),
      ...leaveList.map(a => String(a.student_id)),
    ]);
    const notMarkedList = allStudents.filter(s => !markedIds.has(String(s.ID)));

    setCounts({
      Present:      presentList.length,
      Absent:       absentList.length,
      Leave:        leaveList.length,
      'Not Marked': notMarkedList.length,
    });

    const tabMap = {
      Present:      presentList,
      Absent:       absentList,
      Leave:        leaveList,
      'Not Marked': notMarkedList,
    };
    setRecords(tabMap[tab] || []);
    setLoading(false);
  };

  const markStudent = async (studentId, status) => {
    setMarking(p => ({ ...p, [studentId]: true }));

    const updateRes = await InvokeApi(Config.main_url + apiList.markAttendance, {
      id: String(studentId),
      date,
      status,
    });

    if (updateRes?.code !== '200') {
      const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
      await InvokeApi(Config.main_url + apiList.addAttendance, {
        studentId: String(studentId),
        date,
        checkInTime: now,
        academic_year: acYear,
      });
      if (status !== 'Present') {
        await InvokeApi(Config.main_url + apiList.markAttendance, {
          id: String(studentId),
          date,
          status,
        });
      }
    }

    setMarking(p => ({ ...p, [studentId]: false }));
    loadAll();
  };

  const handleMarkAllAbsent = async () => {
    if (!standard || !section) { alert('Select standard and section first'); return; }
    const notMarked = allStudents.filter(s => {
      const id = String(s.ID);
      return !records.some(r => String(r.student_id) === id);
    });
    if (notMarked.length === 0) { alert('All students are already marked'); return; }
    if (!window.confirm(`Mark ${notMarked.length} unmarked students as Absent for ${standard}-${section} for today?`)) return;

    setLoading(true);
    for (const s of notMarked) {
      await markStudent(s.ID, 'Absent');
    }
    loadAll();
  };

  const classOptions = classes.map(c => ({
    value: `${c.standard?.trim()}||${c.section?.trim()}`,
    label: `${c.standard?.trim()} - ${c.section?.trim()}`,
  }));

  const handleClassChange = (val) => {
    setSelectedClass(val);
    if (val) {
      const [std, sec] = val.split('||');
      setStandard(std);
      setSection(sec);
    } else {
      setStandard('');
      setSection('');
    }
  };

  const activeStatus = STATUSES.find(s => s.key === activeTab);

  const getStudentName = (r) => r.full_name || r.name || '—';
  const getStudentId = (r) => r.student_id || r.ID;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Attendance</Typography>
          <Typography variant="body2" color="text.secondary">{dayjs(date).format('dddd, MMMM D, YYYY')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={handleMarkAllAbsent}
            startIcon={<Cancel />} sx={{ borderRadius: 2, color: '#c62828', borderColor: '#c62828', fontWeight: 600 }}>
            Mark All Absent
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAll} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', mb: 2 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField type="date" size="small" label="Date" value={date}
            onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />

          <TextField select size="small" label="Academic Year" value={acYear}
            onChange={e => setAcYear(e.target.value)} sx={{ minWidth: 160 }}>
            {academicYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Class" value={selectedClass}
            onChange={e => handleClassChange(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">— Select Class —</MenuItem>
            {classOptions.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          <Button variant="contained" onClick={loadAll} disabled={!standard || !section || !acYear}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Load
          </Button>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <Box key={s.key} onClick={() => switchTab(s.key)} sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
            borderRadius: 10, cursor: 'pointer', border: '1.5px solid',
            borderColor: activeTab === s.key ? s.color : '#e8eaf6',
            background: activeTab === s.key ? s.bg : '#fff',
            transition: 'all 0.2s', '&:hover': { background: s.bg },
          }}>
            <Typography fontSize={13} fontWeight={700} color={activeTab === s.key ? s.color : 'text.secondary'}>
              {s.label}
            </Typography>
            <Chip
              label={counts[s.key] ?? 0}
              size="small"
              sx={{
                background: activeTab === s.key ? s.color : '#f0f0f0',
                color: activeTab === s.key ? '#fff' : '#666',
                fontWeight: 700, fontSize: 11, height: 20,
              }}
            />
          </Box>
        ))}
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontSize={14} fontWeight={700} color={activeStatus?.color}>{activeTab}</Typography>
          <Chip label={`${records.length} students`} size="small" sx={{ background: '#f5f6fa', color: 'text.secondary', fontWeight: 600 }} />
          {standard && section && (
            <Chip label={`${standard} - ${section}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Student',
                    activeTab === 'Present' ?  'Check In' : '',
                    activeTab === 'Present' ? 'Check Out' : '',
                    activeTab === 'Not Marked' ? 'Mark Attendance' : 'Status',
                    activeTab !== 'Not Marked' ? 'Change' : ''
                  ].filter(Boolean).map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      {standard && section
                        ? `No ${activeTab} students for ${standard}-${section} on ${dayjs(date).format('DD MMM YYYY')}`
                        : 'Select standard, section and academic year, then click Load'}
                    </TableCell>
                  </TableRow>
                ) : records.map((r, i) => {
                  const sid = getStudentId(r);
                  const isMarking = marking[sid];
                  return (
                    <TableRow key={sid || i} hover>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 30, height: 30, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                            {getStudentName(r)?.[0]}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={12}>{getStudentName(r)}</Typography>
                            <Typography fontSize={10} color="text.secondary">ID: {sid}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {activeTab === 'Present' && (
                      <TableCell sx={{ fontSize: 12 }}>{fmtTime(r.check_in_time) || '—'}</TableCell>
                        )}
                      {activeTab === 'Present' && (
                      <TableCell sx={{ fontSize: 12 }}>{fmtTime(r.check_out_time) || '—'}</TableCell>
                        )}
                      {activeTab === 'Not Marked' && (
                        <TableCell>
                          {isMarking ? (
                            <CircularProgress size={18} sx={{ color: '#1a237e' }} />
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {['Present', 'Absent', 'Leave'].map(s => (
                                <Chip key={s} label={s} size="small" clickable
                                  onClick={() => markStudent(sid, s)}
                                  sx={{
                                    cursor: 'pointer', fontWeight: 700, fontSize: 10,
                                    background: s === 'Present' ? '#e8f5e9' : s === 'Absent' ? '#ffebee' : '#fff3e0',
                                    color: s === 'Present' ? '#2e7d32' : s === 'Absent' ? '#c62828' : '#e65100',
                                    '&:hover': { opacity: 0.75 },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                      )}

                      {activeTab !== 'Not Marked' && (
                        <>
                          <TableCell>
                            <Chip label={r.status || activeTab} size="small"
                              sx={{ background: activeStatus?.bg, color: activeStatus?.color, fontWeight: 700, fontSize: 10 }} />
                          </TableCell>
                          <TableCell>
                            {isMarking ? (
                              <CircularProgress size={18} sx={{ color: '#1a237e' }} />
                            ) : (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {['Present', 'Absent', 'Leave'].filter(s => s !== activeTab).map(s => (
                                  <Chip key={s} label={`→ ${s}`} size="small" clickable
                                    onClick={() => markStudent(sid, s)}
                                    sx={{
                                      cursor: 'pointer', fontWeight: 600, fontSize: 10,
                                      background: s === 'Present' ? '#e8f5e9' : s === 'Absent' ? '#ffebee' : '#fff3e0',
                                      color: s === 'Present' ? '#2e7d32' : s === 'Absent' ? '#c62828' : '#e65100',
                                      '&:hover': { opacity: 0.75 },
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}
